/* eslint-disable @typescript-eslint/no-explicit-any */

import { FormEvent, useEffect, useState } from 'react';
import {
  AutoCompleteField,
  Button,
  Container,
  DropdownField,
  ExportTableButton,
} from '@/components';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { getArmadoresList } from '@/services/getArmadoresList';
import { formatDate } from '@/utils/formatDate';

import {
  AutoCompleteCompleteEvent,
  AutoCompleteChangeEvent,
} from 'primereact/autocomplete';
import { RadioButton } from 'primereact/radiobutton';
import { TableData } from '@/components/Table/types';
import { getAssignTrips } from '@/services/getAssignTrips';
import { getAssignList } from './getAssignList';
import { AssignListInfo } from './types';

import AssignListTable from './components/AssignListTable/AssignListTable';
import { columnsToExport } from './columnsToExport';
import styles from './AssignList.module.scss';

dayjs.extend(customParseFormat);

enum UserType {
  Armador = 'Armador',
  ArmadorSlot = 'Armador de slot',
}

interface AssignListErrors {
  selectedTrip: string;
  selectedShipOwner: string;
}

const FILE_NAME = `AssignList-Montecon`;

const AssignList = () => {
  const { t } = useTranslation('assignList');

  const isNumberFn = (value: any): boolean => {
    return typeof value === 'number' && Number.isFinite(value);
  };

  const { data: tripsData } = useQuery({
    queryKey: ['assignTrips'],
    queryFn: getAssignTrips,
  });

  const { data: assignListDropdown = [] } = useQuery({
    queryKey: ['assignListDropdown'],
    queryFn: getArmadoresList,
  });

  const getAssignListMutation = useMutation({
    mutationFn: getAssignList,
  });

  const [formErrors, setFormErrors] = useState<AssignListErrors>({
    selectedTrip: '',
    selectedShipOwner: '',
  });
  const [filteredTrips, setFilteredTrips] = useState<string[] | undefined>([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [tripCode, setTripCode] = useState<number | null>(null);
  const [selectedUserType, setSelectedUserType] = useState(UserType.Armador);
  const [selectedShipOwner, setSelectedShipOwner] = useState<string | null>(
    null
  );
  const trips = tripsData
    ?.slice()
    .sort(
      (a, b) =>
        new Date(b.comienzoOperaciones).getTime() -
        new Date(a.comienzoOperaciones).getTime()
    )
    .map((item) => {
      let value = item.nombre;

      const date = new Date(item?.comienzoOperaciones);

      const dateFormatted = formatDate(date);

      value = `${value} - ${dateFormatted}`;

      return value;
    });

  const searchTrips = (event: AutoCompleteCompleteEvent) => {
    const filteredTripNames = trips?.filter((name) =>
      name.toLowerCase().includes(event.query.toLowerCase())
    );

    setFilteredTrips(filteredTripNames);
  };

  const handleTripChange = (event: AutoCompleteChangeEvent) => {
    const tripName = event.value;
    setSelectedTrip(tripName);

    const code = tripsData?.find((trip) => {
      const name = (tripName || '').split(' - ')[0];

      return name === trip.nombre;
    })?.codigo;

    if (code) {
      setTripCode(code);
    } else {
      setTripCode(null);
    }
  };

  const validateForm = () => {
    let errors: AssignListErrors = { ...formErrors };
    if (!selectedTrip) {
      errors = {
        ...errors,
        selectedTrip: t('selectedTripRequired'),
      };
    }
    if (!selectedShipOwner) {
      errors = {
        ...errors,
        selectedShipOwner: t('selectedShipOwnerRequired'),
      };
    }
    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    if (tripCode && selectedShipOwner) {
      getAssignListMutation.mutate({
        codViaje: tripCode,
        porSlot: selectedUserType === UserType.ArmadorSlot,
        armador: selectedShipOwner,
      });
    }
  };

  const submitButtonText = t('query');

  const isTableHidden =
    !getAssignListMutation.data && !getAssignListMutation.isPending;

  const handleDropdownChange = (e: { value: string | null }) => {
    setSelectedShipOwner(e.value);
  };

  const options = assignListDropdown?.map((val) => {
    return {
      value: val,
      name: val,
    };
  });

  const [parsedData, setParsedData] = useState<AssignListInfo[]>([]);

  useEffect(() => {
    if (getAssignListMutation.data) {
      const newData = getAssignListMutation.data.map((item: any) => {
        const { ...rest } = item;
        const dayjsDate = dayjs(item?.fecha, 'DD/MM/YYYY HH:mm');
        const fecha = dayjsDate.isValid() ? dayjsDate.toDate() : null;
        const peso = Number(item?.pesoTXT);
        const embarcador = item?.nombreExportador;
        const temperatureWithoutC = item?.temperatura
          ? item?.temperatura
              .replace('CELSIUS', '')
              .replace('C', '')
              .replace(',', '.')
          : null;

        const temp =
          temperatureWithoutC !== null ? Number(temperatureWithoutC) : null;

        return {
          ...rest,
          fecha,
          embarcador,
          peso: isNumberFn(peso) ? peso : '',
          temperatura: temp,
        };
      });
      setParsedData(newData);
    }
  }, [getAssignListMutation.data]);

  return (
    <Container title={t('assignmentList')} className={styles.containerList}>
      <header>
        <form onSubmit={handleSubmit}>
          <AutoCompleteField
            className={styles.autoCompleteField}
            label={t('tripLabel')}
            placeholder={t('tripPlaceholder')}
            value={selectedTrip}
            onChange={handleTripChange}
            suggestions={filteredTrips}
            completeMethod={searchTrips}
            field="trip"
            id="trip"
            required
            error={formErrors.selectedTrip}
          />
          <DropdownField
            id="selectedShipOwner"
            options={options}
            onChange={(e) => handleDropdownChange(e)}
            placeholder={t('selectShipowner')}
            label={t('shipowner')}
            value={selectedShipOwner}
            className={styles.dropdown}
            required
            error={formErrors.selectedShipOwner}
          />

          <div className={styles.radioButtonContainer}>
            <RadioButton
              value={UserType.Armador}
              name="userType"
              inputId="armador"
              onChange={(e) => setSelectedUserType(e.value)}
              checked={selectedUserType === UserType.Armador}
            />
            <label htmlFor="armador">{t('shipowner')}</label>
          </div>
          <div className={styles.radioButtonContainer}>
            <RadioButton
              value={UserType.ArmadorSlot}
              name="userType"
              inputId="armadorSlot"
              onChange={(e) => setSelectedUserType(e.value)}
              checked={selectedUserType === UserType.ArmadorSlot}
            />
            <label htmlFor="armadorSlot">{t('charterer')}</label>
          </div>
          <Button
            type="submit"
            className={styles.submitButton}
            disabled={!tripCode || !selectedShipOwner}
            loading={getAssignListMutation.isPending}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
            {submitButtonText}
          </Button>
        </form>
        {!isTableHidden && (
          <ExportTableButton
            data={parsedData as TableData}
            isDisabled={getAssignListMutation.isPending}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      {!isTableHidden && (
        <AssignListTable
          data={parsedData}
          isLoading={getAssignListMutation.isPending}
        />
      )}
    </Container>
  );
};

export default AssignList;
